import { defineStore } from "pinia";
import { Model, ModelIdType } from "@/interface/model";
import { deepCopy } from "@/utils/tools";
import {
  addLearningLog,
  addSavedModel,
  deleteSavedModel,
  editSavedModel,
  getSavedModel,
  genModelPicture,
} from "@/api/learning";
import { message } from "ant-design-vue";
import { LOG_TYPE } from "@/interface/learning";

interface modelState {
  savedModels: Model[];
  editor: Model;
}

const defaultModel: Model = {
  id: -1,
  name: "New Model",
  description: "This is a new model.",
  create_time: "0",
  update_time: "0",
  layers: [],
  picture: "",
};

export const useModelStore = defineStore("model", {
  state: (): modelState => {
    return {
      savedModels: [],
      editor: deepCopy(defaultModel),
    };
  },
  getters: {
    modelIdToName: (state) => {
      const data: Record<ModelIdType, string> = {};
      for (let i = 0, len = state.savedModels.length; i < len; i++) {
        data[state.savedModels[i].id] = state.savedModels[i].name;
      }
      return data;
    },
    getLayersFromModelId: (state) => (id: ModelIdType | null) => {
      if (!id) {
        throw Error("Bad ID");
      }
      const model = state.savedModels.find((item) => item.id === id);
      if (model === undefined) {
        throw Error(`model id ${id} not found!`);
      }
      return model.layers;
    },
  },
  actions: {
    resetEditor() {
      this.editor = deepCopy(defaultModel);
    },
    getUserSavedModel() {
      getSavedModel().then((res) => {
        this.savedModels = res.data;
      });
    },
    addModelFromEditor() {
      addSavedModel(this.editor).then((res) => {
        const data: Model = res.data;
        this.editor = deepCopy(data);
        this.savedModels.push(data);
        message
          .success(`${this.editor.name} (ID: ${this.editor.id}) Created!`)
          .then();
        return addLearningLog({
          log_type: LOG_TYPE.create_edit_model,
          log: { type: "create", modelId: data.id },
        });
      });
    },
    editModelFromEditor() {
      const modelExist = this.savedModels.find(
        (item) => item.id === this.editor.id
      );
      if (modelExist) {
        editSavedModel(modelExist.id, this.editor).then((res) => {
          const data: Model = res.data;
          this.editor = deepCopy(data);
          modelExist.name = data.name;
          modelExist.description = data.description;
          modelExist.layers = deepCopy(data.layers);
          message
            .success(`${this.editor.name} (ID: ${this.editor.id}) Updated!`)
            .then();
          return addLearningLog({
            log_type: LOG_TYPE.create_edit_model,
            log: { type: "edit", modelId: data.id },
          });
        });
      } else {
        message.error("edit exist model but model not found").then();
      }
    },
    genModelPictureFromEditor() {
      genModelPicture(this.editor).then((res) => {
        this.editor.picture = res.data.url;
      });
    },
    deleteModelByIndex(index: number) {
      deleteSavedModel(this.savedModels[index].id).then(() => {
        return addLearningLog({
          log_type: LOG_TYPE.delete_model,
          log: { modelId: this.savedModels[index].id },
        }).then(() => {
          this.savedModels.splice(index, 1);
        });
      });
    },
  },
});
